import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Clock, Mail, Phone, User, CreditCard, MessageSquareText, ClipboardList, Zap, Video, ExternalLink } from "lucide-react";

const Row = ({ label, children, testid }) => (
  <div className="flex gap-3 text-sm py-1"><span className="w-28 shrink-0 text-slate-500">{label}</span><span className="text-slate-800 min-w-0 break-words" data-testid={testid}>{children || "—"}</span></div>
);
const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-xl border border-slate-200 p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 mb-1"><Icon className="h-3.5 w-3.5" /> {title}</div>
    {children}
  </div>
);
const money = (n, cur) => (n || n === 0 ? `$${Number(n).toFixed(2)} ${cur || "CAD"}` : "");

// Appointment card → full details: who booked, what they wrote, payment and any questionnaire answers.
export function AppointmentDetailDialog({ id, canManage, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const load = () => api.get(`/appointments/${id}/details`).then((r) => setA(r.data)).catch((e) => { toast.error(apiErr(e)); onClose(); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  const viewReceipt = async () => {
    try { const r = await api.get(`/appointments/${id}/receipt`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const markReceived = async () => {
    try { await api.post(`/appointments/${id}/payment-received`); toast.success("Payment marked as received"); load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const c = a?.contact || {};
  const p = a?.payment || {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="appt-detail-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2">{a?.is_emergency && <Zap className="h-4 w-4 text-primary" />}{a ? `${a.type} — ${c.name}` : "Loading…"}</DialogTitle></DialogHeader>
        {a && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <Clock className="h-4 w-4 text-slate-400" /><span data-testid="appt-detail-when">{a.date} · {a.start_time}{a.end_time ? `–${a.end_time}` : ""}</span>
              <StatusBadge status={a.status} />
              {a.is_emergency && <span className="text-[10px] font-bold uppercase bg-primary/10 text-primary px-1.5 py-0.5 rounded">Emergency</span>}
              <span className="text-xs text-slate-400">{a.source === "public" ? "Booked online" : "Booked by staff"}</span>
            </div>

            <Section icon={User} title="Contact" testid="appt-detail-contact">
              <Row label="Name" testid="appt-detail-name">{c.id && c.type === "client" ? <Link to={`/admin/clients/${c.id}`} onClick={onClose} className="text-primary font-semibold hover:underline">{c.name}</Link> : c.name}{c.type && <span className="ml-1 text-[10px] font-bold uppercase bg-slate-100 text-slate-600 px-1 rounded">{c.type}</span>}</Row>
              <Row label="Email">{c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-primary"><Mail className="h-3.5 w-3.5" />{c.email}</a>}</Row>
              <Row label="Phone">{c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-primary"><Phone className="h-3.5 w-3.5" />{c.phone}</a>}</Row>
              {c.file_number && <Row label="File #">{c.file_number}</Row>}
              {c.service_interested && <Row label="Interested in">{c.service_interested}</Row>}
            </Section>

            <Section icon={MessageSquareText} title="What the client told us" testid="appt-detail-answers">
              <Row label="Meeting">{a.meeting_type}{a.staff_name ? ` · with ${a.staff_name}` : ""}</Row>
              {a.meet_link && <Row label="Link"><a href={a.meet_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><Video className="h-3.5 w-3.5" /> Join Google Meet</a></Row>}
              <Row label={a.is_emergency ? "Reason" : "Message"} testid="appt-detail-notes">{a.notes && <span className="whitespace-pre-wrap">{a.notes}</span>}</Row>
            </Section>

            <Section icon={CreditCard} title="Payment" testid="appt-detail-payment">
              <Row label="Method" testid="appt-detail-pay-method">{p.method || (a.source === "public" ? "No payment needed" : "Staff booking (free)")}</Row>
              {p.booking_code && <Row label="Free code" testid="appt-detail-code"><span className="font-mono">{p.booking_code}</span></Row>}
              {(p.amount_due || p.amount_due === 0) && <Row label="Amount due">{money(p.amount_due, p.currency)}</Row>}
              {p.amount_paid ? <Row label="Paid" testid="appt-detail-paid">{money(p.amount_paid, p.currency)}{p.paid_at ? ` · ${p.paid_at.slice(0, 10)}` : ""}</Row> : null}
              {p.status && <Row label="Status" testid="appt-detail-pay-status">{p.status}{p.verified_by ? ` · checked by ${p.verified_by}` : ""}</Row>}
              {a.price ? <Row label="Emergency fee">{money(a.price, p.currency)}</Row> : null}
              {p.has_receipt && (
                <div className="flex gap-2 pt-1">
                  <button data-testid="appt-detail-receipt" onClick={viewReceipt} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-primary hover:text-primary inline-flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" /> View receipt</button>
                  {canManage && p.status !== "Payment received" && <button data-testid="appt-detail-mark-received" onClick={markReceived} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white">Mark received</button>}
                </div>
              )}
            </Section>

            {a.questionnaires?.length > 0 && (
              <Section icon={ClipboardList} title="Questionnaire answers" testid="appt-detail-questionnaires">
                {a.questionnaires.map((q) => (
                  <div key={q.id} className="py-1">
                    <div className="text-xs font-semibold text-slate-600 mb-1">{q.set_name} · {(q.submitted_at || "").slice(0, 10)}</div>
                    {q.answers.map((x) => <Row key={x.id} label={x.label}>{x.answer}</Row>)}
                  </div>
                ))}
              </Section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
